/**
 * SignShopFilters — grouped filter sidebar + search + sort for the Sign Shop.
 *
 * Added §28.518.99.58. Owner-approved UI upgrade replacing the flat pill-row
 * filter with a logical grouping: Product Type, Material/Finish, Use Case,
 * Variants. Works over the Printify product payload already used by SignShop.
 *
 * All grouping is derived client-side from product title + tags + variant
 * title keywords so no schema migration or backend change is required for v1.
 */
import React, { useMemo } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface FilterableVariant {
  id: number;
  title: string;
  is_enabled: boolean;
  is_available: boolean;
}

export interface FilterableProduct {
  id: string;
  title: string;
  description: string;
  tags: string[];
  variants: FilterableVariant[];
}

export type FilterGroup = 'type' | 'material' | 'use_case' | 'variant';

export interface FilterState {
  type: string[];
  material: string[];
  use_case: string[];
  variant: string[];
}

export type SortKey =
  | 'price_asc'
  | 'price_desc'
  | 'newest'
  | 'bestsellers'
  | 'alpha_asc';

export const EMPTY_FILTERS: FilterState = {
  type: [],
  material: [],
  use_case: [],
  variant: [],
};

// ── Keyword dictionaries (v1 client-side derivation) ────────────────────────

const TYPE_RULES: Array<[string, RegExp]> = [
  ['Sticker', /\bsticker|decal\b/i],
  ['Metal Sign', /\bmetal\b/i],
  ['Acrylic Sign', /\bacrylic\b/i],
  ['Wood Sign', /\bwood\b/i],
  ['Aluminum Sign', /\baluminum\b/i],
  ['Canvas', /\bcanvas\b/i],
  ['Banner', /\bbanner\b/i],
  ['Poster', /\bposter\b/i],
  ['Apparel', /\b(t-?shirt|hoodie|sweatshirt|apparel)\b/i],
  ['Hardware', /\bhardware|hook|bracket|mount\b/i],
];

const MATERIAL_RULES: Array<[string, RegExp]> = [
  ['Matte', /\bmatte\b/i],
  ['Glossy', /\bgloss(y)?\b/i],
  ['Satin', /\bsatin\b/i],
  ['Vinyl', /\bvinyl\b/i],
  ['Paper', /\bpaper\b/i],
  ['DTF', /\bdtf\b/i],
  ['Kiss-Cut', /\bkiss[- ]?cut\b/i],
  ['Die-Cut', /\bdie[- ]?cut\b/i],
  ['Waterproof', /\bwaterproof|weatherproof\b/i],
];

const USE_CASE_RULES: Array<[string, RegExp]> = [
  ['Outdoor', /\boutdoor|exterior|weatherproof\b/i],
  ['Indoor', /\bindoor|interior\b/i],
  ['Home Decor', /\bhome|decor|wall\b/i],
  ['Business', /\bbusiness|office|commercial|storefront\b/i],
  ['Property Signage', /\bproperty|building|entrance|door|intercom\b/i],
];

function matchRules(rules: Array<[string, RegExp]>, hay: string): string[] {
  const matched: string[] = [];
  for (const [label, re] of rules) {
    if (re.test(hay)) matched.push(label);
  }
  return matched;
}

function haystack(p: FilterableProduct): string {
  return [p.title, p.description, ...(p.tags || [])].filter(Boolean).join(' ');
}

// Variant labels are derived from the variant title text (e.g. "3" x 4" / Matte").
// We only keep short, size-ish tokens to avoid label explosion.
function variantLabels(p: FilterableProduct): string[] {
  const set = new Set<string>();
  for (const v of p.variants || []) {
    if (!v.is_enabled) continue;
    // Grab size tokens like `3" x 4"`, `Small`, `Medium`, `Large`, `Vertical`, `Horizontal`
    const t = v.title;
    const sizes = t.match(/\d+(?:\.\d+)?\s*"?\s*[x×]\s*\d+(?:\.\d+)?\s*"?/gi) || [];
    for (const s of sizes) set.add(s.replace(/\s+/g, ' ').trim());
    for (const word of ['Small', 'Medium', 'Large', 'XL', 'Vertical', 'Horizontal', 'Square']) {
      if (new RegExp(`\\b${word}\\b`, 'i').test(t)) set.add(word);
    }
  }
  return Array.from(set);
}

// ── Derivation ──────────────────────────────────────────────────────────────

export interface ProductFacets {
  type: string[];
  material: string[];
  use_case: string[];
  variant: string[];
}

export function deriveFacets(p: FilterableProduct): ProductFacets {
  const hay = haystack(p);
  return {
    type: matchRules(TYPE_RULES, hay),
    material: matchRules(MATERIAL_RULES, hay),
    use_case: matchRules(USE_CASE_RULES, hay),
    variant: variantLabels(p),
  };
}

// ── Filter + search + sort application ──────────────────────────────────────

export interface ApplyOpts {
  products: FilterableProduct[];
  filters: FilterState;
  search: string;
  sort: SortKey;
  // Product id → order count (from sign_orders); optional.
  bestsellerRank?: Record<string, number>;
}

export function applyFilterSortSearch({
  products,
  filters,
  search,
  sort,
  bestsellerRank,
}: ApplyOpts): FilterableProduct[] {
  const q = search.trim().toLowerCase();
  const filtered = products.filter(p => {
    const facets = deriveFacets(p);
    // Every active group must have at least one overlap (multi-select = OR within a group, AND across groups)
    if (filters.type.length && !filters.type.some(t => facets.type.includes(t))) return false;
    if (filters.material.length && !filters.material.some(m => facets.material.includes(m))) return false;
    if (filters.use_case.length && !filters.use_case.some(u => facets.use_case.includes(u))) return false;
    if (filters.variant.length && !filters.variant.some(v => facets.variant.includes(v))) return false;
    if (q) {
      const hay = haystack(p).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const price = (p: FilterableProduct) => {
    const prices = p.variants.filter(v => v.is_enabled).map((v: any) => v.price).filter((n: number) => n > 0);
    return prices.length ? Math.min(...prices) : Number.POSITIVE_INFINITY;
  };

  const sorted = [...filtered];
  switch (sort) {
    case 'price_asc':
      sorted.sort((a, b) => price(a) - price(b));
      break;
    case 'price_desc':
      sorted.sort((a, b) => price(b) - price(a));
      break;
    case 'newest':
      // Printify returns newest-first already; leave order.
      break;
    case 'bestsellers':
      if (bestsellerRank) {
        sorted.sort((a, b) => (bestsellerRank[b.id] || 0) - (bestsellerRank[a.id] || 0));
      } else {
        // v1 fallback: alphabetical proxy
        sorted.sort((a, b) => a.title.localeCompare(b.title));
      }
      break;
    case 'alpha_asc':
      sorted.sort((a, b) => a.title.localeCompare(b.title));
      break;
  }
  return sorted;
}

// ── Counts (for "(N)" hints next to each option) ────────────────────────────

/**
 * Count how many products would match if `group=option` were the only extra
 * filter added on top of the current active filters. Enables the "(N)" hint.
 */
export function computeFacetCounts(
  products: FilterableProduct[],
  filters: FilterState,
  search: string,
): Record<FilterGroup, Record<string, number>> {
  const out: Record<FilterGroup, Record<string, number>> = {
    type: {},
    material: {},
    use_case: {},
    variant: {},
  };
  const groups: FilterGroup[] = ['type', 'material', 'use_case', 'variant'];
  for (const g of groups) {
    // For counting, hold `g` empty (so this group doesn't self-restrict) and
    // count products by facet in this group after the OTHER filters apply.
    const subFilters: FilterState = { ...filters, [g]: [] } as FilterState;
    const subset = applyFilterSortSearch({
      products,
      filters: subFilters,
      search,
      sort: 'newest',
    });
    for (const p of subset) {
      const facets = deriveFacets(p);
      for (const label of facets[g]) {
        out[g][label] = (out[g][label] || 0) + 1;
      }
    }
  }
  return out;
}

// ── UI ──────────────────────────────────────────────────────────────────────

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'newest', label: 'Newest' },
  { value: 'bestsellers', label: 'Bestsellers' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'alpha_asc', label: 'Alphabetical A–Z' },
];

const GROUP_LABELS: Record<FilterGroup, string> = {
  type: 'Product Type',
  material: 'Material / Finish',
  use_case: 'Use Case',
  variant: 'Variants',
};

interface FilterGroupSectionProps {
  group: FilterGroup;
  options: string[];
  selected: string[];
  counts: Record<string, number>;
  onToggle: (group: FilterGroup, option: string) => void;
}

const FilterGroupSection: React.FC<FilterGroupSectionProps> = ({
  group,
  options,
  selected,
  counts,
  onToggle,
}) => {
  const [open, setOpen] = React.useState(true);
  if (options.length === 0) return null;
  return (
    <div className="border-b border-gray-200 py-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-left text-sm font-semibold text-gray-900 mb-2"
        aria-expanded={open}
      >
        <span>{GROUP_LABELS[group]}</span>
        <span className="text-gray-400 text-xs">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="space-y-1.5">
          {options.map(opt => {
            const isChecked = selected.includes(opt);
            const count = counts[opt] || 0;
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 text-sm cursor-pointer px-1 py-0.5 rounded hover:bg-gray-50 ${
                  count === 0 && !isChecked ? 'opacity-40' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggle(group, opt)}
                  className="accent-emerald-600"
                />
                <span className="flex-1 text-gray-700">{opt}</span>
                <span className="text-xs text-gray-400">({count})</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface SignShopFiltersProps {
  products: FilterableProduct[];
  filters: FilterState;
  search: string;
  sort: SortKey;
  onFiltersChange: (next: FilterState) => void;
  onSearchChange: (next: string) => void;
  onSortChange: (next: SortKey) => void;
  /** For mobile bottom-sheet parent, hide the sidebar chrome. */
  variant?: 'sidebar' | 'inline';
  className?: string;
}

/**
 * Sidebar variant of the filter panel. Search + sort live above the grid;
 * this component renders only the grouped filter checkboxes.
 */
export const SignShopFilters: React.FC<SignShopFiltersProps> = ({
  products,
  filters,
  search,
  onFiltersChange,
  variant = 'sidebar',
  className = '',
}) => {
  const allOptions = useMemo(() => {
    const acc: Record<FilterGroup, Set<string>> = {
      type: new Set(),
      material: new Set(),
      use_case: new Set(),
      variant: new Set(),
    };
    for (const p of products) {
      const f = deriveFacets(p);
      f.type.forEach(x => acc.type.add(x));
      f.material.forEach(x => acc.material.add(x));
      f.use_case.forEach(x => acc.use_case.add(x));
      f.variant.forEach(x => acc.variant.add(x));
    }
    return {
      type: Array.from(acc.type).sort(),
      material: Array.from(acc.material).sort(),
      use_case: Array.from(acc.use_case).sort(),
      variant: Array.from(acc.variant).sort(),
    };
  }, [products]);

  const counts = useMemo(
    () => computeFacetCounts(products, filters, search),
    [products, filters, search],
  );

  const toggle = (group: FilterGroup, option: string) => {
    const current = filters[group];
    const next = current.includes(option)
      ? current.filter(x => x !== option)
      : [...current, option];
    onFiltersChange({ ...filters, [group]: next });
  };

  const anyActive =
    filters.type.length + filters.material.length + filters.use_case.length + filters.variant.length > 0;

  const shell =
    variant === 'sidebar'
      ? 'bg-white border border-gray-200 rounded-lg p-3'
      : '';

  return (
    <aside className={`${shell} ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Filters</div>
        {anyActive && (
          <button
            type="button"
            onClick={() => onFiltersChange(EMPTY_FILTERS)}
            className="text-xs text-emerald-700 hover:text-emerald-800"
          >
            Clear all
          </button>
        )}
      </div>
      {(['type', 'material', 'use_case', 'variant'] as FilterGroup[]).map(g => (
        <FilterGroupSection
          key={g}
          group={g}
          options={allOptions[g]}
          selected={filters[g]}
          counts={counts[g]}
          onToggle={toggle}
        />
      ))}
    </aside>
  );
};

// ── Toolbar (search + sort + result count + active chips) ───────────────────

interface SignShopToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  sort: SortKey;
  onSortChange: (v: SortKey) => void;
  filters: FilterState;
  onFiltersChange: (next: FilterState) => void;
  resultCount: number;
  totalCount: number;
  onOpenMobileFilters?: () => void;
}

export const SignShopToolbar: React.FC<SignShopToolbarProps> = ({
  search,
  onSearchChange,
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  resultCount,
  totalCount,
  onOpenMobileFilters,
}) => {
  const chips: Array<{ group: FilterGroup; value: string }> = [];
  (['type', 'material', 'use_case', 'variant'] as FilterGroup[]).forEach(g => {
    filters[g].forEach(v => chips.push({ group: g, value: v }));
  });

  const removeChip = (group: FilterGroup, value: string) => {
    onFiltersChange({ ...filters, [group]: filters[group].filter(v => v !== value) });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        {onOpenMobileFilters && (
          <button
            type="button"
            onClick={onOpenMobileFilters}
            className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <span>☰</span> Filters
            {chips.length > 0 && (
              <span className="ml-1 bg-emerald-600 text-white text-xs rounded-full px-1.5">
                {chips.length}
              </span>
            )}
          </button>
        )}
        <div className="relative flex-1 min-w-0">
          <input
            type="text"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
            placeholder="Search signs, stickers, hardware…"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-sm"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 whitespace-nowrap">Sort by</label>
          <select
            value={sort}
            onChange={e => onSortChange(e.target.value as SortKey)}
            className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          {chips.map(c => (
            <span
              key={`${c.group}-${c.value}`}
              className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full pl-2.5 pr-1.5 py-0.5 text-xs"
            >
              <span className="text-emerald-600/70">{GROUP_LABELS[c.group]}:</span>
              <span className="font-medium">{c.value}</span>
              <button
                type="button"
                onClick={() => removeChip(c.group, c.value)}
                className="ml-0.5 rounded-full hover:bg-emerald-100 w-4 h-4 flex items-center justify-center"
                aria-label={`Remove ${c.value}`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => onFiltersChange(EMPTY_FILTERS)}
            className="text-xs text-gray-500 hover:text-gray-800 ml-1"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="text-xs text-gray-500">
        Showing {resultCount} of {totalCount} products
      </div>
    </div>
  );
};

// ── Empty state ─────────────────────────────────────────────────────────────

interface EmptyStateProps {
  onClear: () => void;
}

export const SignShopEmptyState: React.FC<EmptyStateProps> = ({ onClear }) => (
  <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center bg-white">
    <div className="text-4xl mb-2">🔎</div>
    <div className="text-gray-900 font-semibold mb-1">No signs match your filters</div>
    <div className="text-sm text-gray-500 mb-4">
      Try loosening a filter or clearing your search.
    </div>
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
    >
      Clear all filters
    </button>
  </div>
);
